/**
 * infinite-scroll-collection.js
 * -----------------------------------------------------------------------
 * Custom element that turns Dawn's paginated collection grid into
 * infinite-scroll / "load more" — fully additive, no core Dawn files
 * touched by this script.
 *
 * The section (sections/main-collection-product-grid.liquid) wraps the
 * product grid + pagination in:
 *
 *   <infinite-scroll-collection
 *     data-enabled="{{ section.settings.enable_infinite_scroll }}"
 *     data-section-id="{{ section.id }}"
 *     data-mode="{{ section.settings.infinite_scroll_mode }}"
 *     data-offset="{{ section.settings.infinite_scroll_offset }}"
 *     data-next-url="{{ paginate.next.url }}"
 *     data-load-more-label="{{ section.settings.infinite_scroll_load_more_label }}"
 *     data-loading-label="{{ section.settings.infinite_scroll_loading_label }}"
 *     style="display: contents;">
 *     <div class="collection ..." data-infinite-scroll-container>
 *       <ul id="product-grid" data-infinite-scroll-grid>...</ul>
 *       {% render 'pagination' %}
 *     </div>
 *   </infinite-scroll-collection>
 *
 * Why data-next-url instead of parsing Dawn's rendered pagination link:
 * Dawn's snippets/pagination.liquid markup can vary by version/customization,
 * so scraping its anchor tags is fragile. `paginate.next.url` is a stable
 * Liquid object available wherever `{% paginate %}` is used, so it's passed
 * straight through as a data attribute instead.
 *
 * Why no MutationObserver: when a customer changes filters/sort, Dawn's
 * facets.js replaces #ProductGridContainer's innerHTML wholesale. That
 * removes this custom element from the DOM and inserts a freshly rendered
 * one — the browser natively fires disconnectedCallback on the old instance
 * and connectedCallback on the new one, so state simply resets itself with
 * no manual re-render detection needed.
 * -----------------------------------------------------------------------
 */

if (!customElements.get('infinite-scroll-collection')) {
  class InfiniteScrollCollection extends HTMLElement {
    constructor() {
      super();
      this.loading = false;
      this.onIntersect = this.onIntersect.bind(this);
    }

    connectedCallback() {
      // Theme editor toggle is off — leave Dawn's native pagination alone.
      if (this.dataset.enabled !== 'true') return;

      this.sectionId = this.dataset.sectionId;
      this.mode = this.dataset.mode === 'button' ? 'button' : 'auto';
      this.offset = parseInt(this.dataset.offset, 10);
      if (Number.isNaN(this.offset)) this.offset = 600;
      this.nextUrl = this.dataset.nextUrl || '';
      this.loadMoreLabel = this.dataset.loadMoreLabel || 'Load more';
      this.loadingLabel = this.dataset.loadingLabel || 'Loading…';

      this.grid = this.querySelector('[data-infinite-scroll-grid]');
      this.paginationWrapper = this.querySelector('.pagination-wrapper');

      // Single page of results — nothing to load, nothing to do.
      if (!this.grid || !this.nextUrl) return;

      if (this.paginationWrapper) {
        this.paginationWrapper.classList.add('infinite-scroll-hidden');
      }

      this.buildTrigger();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    buildTrigger() {
      if (this.mode === 'button') {
        this.loadMoreBtn = document.createElement('button');
        this.loadMoreBtn.type = 'button';
        this.loadMoreBtn.className = 'infinite-scroll-load-more button button--secondary';
        this.loadMoreBtn.textContent = this.loadMoreLabel;
        this.loadMoreBtn.addEventListener('click', () => this.loadNextPage());
        this.append(this.loadMoreBtn);
      } else {
        this.sentinel = document.createElement('div');
        this.sentinel.className = 'infinite-scroll-sentinel';
        this.sentinel.setAttribute('aria-hidden', 'true');
        this.append(this.sentinel);

        this.observer = new IntersectionObserver(this.onIntersect, {
          root: null,
          rootMargin: `${this.offset}px 0px`,
          threshold: 0,
        });
        this.observer.observe(this.sentinel);
      }
    }

    onIntersect(entries) {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !this.loading) this.loadNextPage();
      });
    }

    async loadNextPage() {
      if (!this.nextUrl || this.loading) return;
      this.loading = true;
      this.classList.add('infinite-scroll-loading');
      if (this.loadMoreBtn) {
        this.loadMoreBtn.disabled = true;
        this.loadMoreBtn.textContent = this.loadingLabel;
      }

      try {
        const url = new URL(this.nextUrl, window.location.origin);
        url.searchParams.set('section_id', this.sectionId);

        const response = await fetch(url.toString());
        if (!response.ok) throw new Error(`Infinite scroll fetch failed: ${response.status}`);
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');

        const freshHost = doc.querySelector('infinite-scroll-collection');
        const newGrid = doc.querySelector('[data-infinite-scroll-grid]');

        if (newGrid && this.grid) {
          Array.from(newGrid.children).forEach((child) => {
            this.grid.appendChild(document.importNode(child, true));
          });
        }

        // The freshly fetched page carries the *next* next-url (or none,
        // once we've reached the last page).
        this.nextUrl = freshHost ? freshHost.dataset.nextUrl || '' : '';

        // Deliberately NOT touching the address bar: Dawn's pagination is
        // server-rendered per page, so if the URL were updated to ?page=2
        // and the customer refreshed, Shopify would render only page 2's
        // products instead of the full accumulated grid. Leaving the URL
        // on the original request means a refresh always cleanly restarts
        // from page 1.

        if (!this.nextUrl) {
          this.teardown();
        } else if (this.loadMoreBtn) {
          this.loadMoreBtn.disabled = false;
          this.loadMoreBtn.textContent = this.loadMoreLabel;
        }

        this.dispatchEvent(new CustomEvent('infinite-scroll:page-loaded', { bubbles: true }));
      } catch (err) {
        console.error(err);
        // Fail safe: reveal Dawn's native pagination so customers can still
        // navigate manually if a fetch ever fails.
        if (this.paginationWrapper) {
          this.paginationWrapper.classList.remove('infinite-scroll-hidden');
        }
        this.teardown();
      } finally {
        this.loading = false;
        this.classList.remove('infinite-scroll-loading');
      }
    }

    teardown() {
      if (this.observer) this.observer.disconnect();
      if (this.sentinel) this.sentinel.remove();
      if (this.loadMoreBtn) this.loadMoreBtn.remove();
    }
  }

  customElements.define('infinite-scroll-collection', InfiniteScrollCollection);
}